import {
	authHeaders,
	useAuthTokenGetter,
} from "@memorang/ui/contexts/AuthTokenContext";
import { fetch as expoFetch } from "expo/fetch";
import { useCallback, useEffect, useState } from "react";
import { ENDPOINT_SESSION_ANALYSIS } from "../constants";

type Params = {
	sessionId: number;
	examName: string;
	tenantName: string;
	outputLanguage?: string;
	examId: string;
	appId: string;
};

export const useSessionAnalysis = ({
	sessionId,
	examName,
	tenantName,
	outputLanguage,
	examId,
	appId,
}: Params) => {
	const getAuthToken = useAuthTokenGetter();
	const [sessionAnalysis, setSessionAnalysis] = useState("");
	const [loading, setLoading] = useState(false);
	const [streaming, setStreaming] = useState(false);

	// biome-ignore lint/correctness/useExhaustiveDependencies: // TODO fix me later
	const fetchSessionAnalysis = useCallback(
		// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: na
		async (signal: AbortSignal) => {
			if (!sessionId) {
				return;
			}

			setLoading(true);
			setStreaming(false);

			try {
				const token = await getAuthToken();
				if (signal.aborted) {
					return;
				}
				const response = await expoFetch(ENDPOINT_SESSION_ANALYSIS, {
					method: "POST",
					signal,
					headers: {
						"Content-Type": "application/json",
						Accept: "text/event-stream",
						...authHeaders(token),
					},
					body: JSON.stringify({
						sessionId,
						examName,
						tenantName,
						outputLanguage,
						examId: `${examId}`,
						useSupabaseStore: true,
						appId,
					}),
				});

				if (!response.ok) {
					throw new Error(
						`Failed to fetch session analysis: ${response.status} ${response.statusText}`,
					);
				}

				const reader = await response.body?.getReader();
				let result = "";
				const encoder = new TextDecoder();
				let done = false;
				setLoading(false);
				setStreaming(true);
				while (!done && reader) {
					const { value, done: _done } = await reader.read();
					done = _done;
					if (signal.aborted) {
						await reader.cancel();
						return;
					}
					if (value) {
						result += encoder.decode(value, { stream: !done });
					}
					if (!signal.aborted) {
						setSessionAnalysis(result);
					}
				}
			} catch (error) {
				if (signal.aborted) {
					return;
				}
				console.error("Error fetching session analysis:", error);
				setSessionAnalysis("");
			} finally {
				if (!signal.aborted) {
					setLoading(false);
					setStreaming(false);
				}
			}
		},
		[sessionId],
	);

	useEffect(() => {
		const controller = new AbortController();
		fetchSessionAnalysis(controller.signal);
		return () => {
			controller.abort();
		};
	}, [fetchSessionAnalysis]);
	return {
		sessionAnalysis,
		loading,
		streaming,
	};
};
